import { archiveFailed, unarchiveFailed } from '../../util/archiveOutcome';

/**
 * Did the action a tutorial teaches actually succeed?
 *
 * This used to be answered by `emitTutorialActionResult(...)` calls hand-wired
 * into the components that perform those actions — twelve of them across
 * `AssignRecord`, `SplitSelectedRecord`, `UploadSingleRecord` and
 * `UploadedRecords`, each carrying a paragraph of tutorial rationale in a file
 * that otherwise knows nothing about tutorials, on top of a `window` CustomEvent
 * bus. Every one of those outcomes was ALREADY on the wire as a redux action, so
 * the whole transport was a second, parallel copy of something the store
 * broadcasts for free.
 *
 * Now there is one table. It is the single place that says which redux action
 * settles which taught action and how to read the result, and it lives next to
 * the tutorial definitions that consume those names.
 *
 * WHY A MAP OF FUNCTIONS rather than `type -> boolean`: for two of these, the
 * HTTP status is not the answer. Both archive endpoints scope by
 * organization/department, silently drop tokens that do not match, and still
 * return 200 — so a success response that archived nothing is a failure. The
 * same `archiveFailed`/`unarchiveFailed` helpers the UI uses decide it here, so
 * the tutorial and the toast can never disagree.
 */

/** The taught actions a tutorial may declare as its `completesOn`. */
export const TUTORIAL_ACTIONS = {
  upload: 'upload',
  archive: 'archive',
  send: 'send',
  chart: 'chart',
  assign: 'assign',
  split: 'split',
};

const settled = (action, success) => ({ action, success });

/**
 * Not every terminal-looking response settles anything.
 *
 * A 200 from `assignDocument` carrying `is_department_user: false` is
 * PRELIMINARY: no AssignedDocument exists yet, and whether one ever does depends
 * on the access-grant prompt the UI then shows. Reporting success here completed
 * the assign tutorial for an assignment that had not happened — and if the user
 * answers No it never happens at all, while the checkmark stays. The forced
 * retry that follows a Yes dispatches this action again, without the flag, and
 * that one is real.
 *
 * Answering No emits nothing rather than a failure, on purpose: the user
 * abandoned the flow, they did not hit an error. With no result the tour stays on
 * its step so they can pick a different assignee and try again.
 */
const NOT_SETTLED = null;

export const TUTORIAL_OUTCOME_ACTIONS = {
  // Uploading a document. The success path hard-navigates to the viewer
  // immediately afterwards, which is why subscribers are notified synchronously
  // during the dispatch — see `reportTutorialActionResult`.
  FINISHED_UPLOAD_RECORD: () => settled(TUTORIAL_ACTIONS.upload, true),
  FAILED_UPLOAD_RECORD: () => settled(TUTORIAL_ACTIONS.upload, false),

  // Archiving. Reported as `archive` from BOTH directions on purpose: the
  // tutorial teaches one button, and in the archived view (query-string state on
  // the same path, so the route guard never fires) that button unarchives.
  // Clearing a record out of the list you are looking at is the lesson either way.
  FINISHED_ARCHIVE_RECORDS: (payload) => settled(TUTORIAL_ACTIONS.archive, !archiveFailed(payload)),
  FAILED_ARCHIVE_RECORDS: () => settled(TUTORIAL_ACTIONS.archive, false),
  FINISHED_UNARCHIVE_RECORD: (payload) => settled(TUTORIAL_ACTIONS.archive, !unarchiveFailed(payload)),
  FAILED_UNARCHIVE_RECORD: () => settled(TUTORIAL_ACTIONS.archive, false),

  // Sending out of Medsender. Four endpoints reach the same Send button —
  // recipients, patient, distribution list, and the bulk path for a
  // multi-selection — so all four settle the one taught action.
  FINISHED_SEND_TO_MULTIPLE_RECIPIENTS: () => settled(TUTORIAL_ACTIONS.send, true),
  FAILED_SEND_TO_MULTIPLE_RECIPIENTS: () => settled(TUTORIAL_ACTIONS.send, false),
  FINISHED_SEND_PATIENT_RECORD: () => settled(TUTORIAL_ACTIONS.send, true),
  FAILED_SEND_PATIENT_RECORD: () => settled(TUTORIAL_ACTIONS.send, false),
  FINISHED_SEND_TO_DISTRIBUTION_LIST: () => settled(TUTORIAL_ACTIONS.send, true),
  FAILED_SEND_TO_DISTRIBUTION_LIST: () => settled(TUTORIAL_ACTIONS.send, false),
  FINISHED_SEND_BULK_RECORDS: () => settled(TUTORIAL_ACTIONS.send, true),
  FAILED_SEND_BULK_RECORDS: () => settled(TUTORIAL_ACTIONS.send, false),

  // Filing to a patient chart. This one had NO signal at all before — the chart
  // actions open a drawer and the real upload happens later inside it, so the
  // send-to-chart tutorial had to infer completion from its target disappearing
  // (`completesOnVanish`), which is what let a cancelled dropdown look like a
  // filed document. The drawer and the legacy auto-upload modal both go through
  // `createChartUpload`, so both are covered by these two lines.
  FINISH_AUTOUPLOAD: () => settled(TUTORIAL_ACTIONS.chart, true),
  FAILED_AUTOUPLOAD: () => settled(TUTORIAL_ACTIONS.chart, false),

  // Assigning to a teammate. See NOT_SETTLED above for the preliminary response.
  FINISHED_CREATE_ASSIGNED_RECORD: (payload) => (
    payload?.data?.is_department_user === false
      ? NOT_SETTLED
      : settled(TUTORIAL_ACTIONS.assign, true)
  ),
  FAILED_CREATE_ASSIGNED_RECORD: () => settled(TUTORIAL_ACTIONS.assign, false),

  // Splitting a document into several records.
  FINISH_SPLIT_RECORD: () => settled(TUTORIAL_ACTIONS.split, true),
  FAILED_SPLIT_RECORD: () => settled(TUTORIAL_ACTIONS.split, false),
};

/** The outcome a redux action settles, or null if it settles nothing. */
export const outcomeForReduxAction = (action) => {
  const read = action && TUTORIAL_OUTCOME_ACTIONS[action.type];
  if (!read) return null;
  return read(action.payload) || null;
};

const listeners = new Set();

/**
 * Subscribe to taught-action outcomes. Returns an unsubscribe function.
 *
 * A module-level set rather than redux state, for one hard reason: some taught
 * actions tear the page down. A successful single upload ends in
 * `window.open('/app/view/:id', '_self')` — a hard navigation that destroys the
 * JS context — so the completion has to be written to localStorage DURING the
 * dispatch, strictly before the effect that navigates. Routing the outcome
 * through redux state would make it arrive on a React re-render, which is one
 * turn too late: the user finishes the task and the checklist still shows it
 * unfinished.
 */
export const onTutorialActionResult = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Notify subscribers, synchronously. The only caller is
 * `tutorialOutcomeMiddleware` — the whole point of the table above is that there
 * is exactly one producer.
 */
export const reportTutorialActionResult = (action, success) => {
  listeners.forEach((listener) => listener(action, success === true));
};
